import { useQuery } from '@tanstack/react-query';
import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import { addDays, format, isValid, parse } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { apiAdminemailsGetCollection } from '@r/api/generated/client.ts';
import type {
  ApiAdminemailsGetCollectionParams,
  EmailEmailReadModel
} from '@r/api/generated/client.schemas.ts';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

import { useEmailHistoryColumns, useEmailHistoryFilterFields } from './email-history-columns.tsx';
import { type EmailHistorySource, getEmailHistoryQueryKey } from './email-history-query-key.ts';

/** L'API plafonne `perPage` à 30. */
const PAGE_SIZE_OPTIONS = [10, 20, 30];

const SORTABLE_FIELDS = ['sentAt', 'statusUpdatedAt', 'status'];

type EmailsQueryResult = {
  data: EmailEmailReadModel[];
  pagination?: { last?: number; totalItems?: number };
};

function getFilterValue<T>(columnFilters: ColumnFiltersState, id: string) {
  return columnFilters.find((filter) => filter.id === id)?.value as T | undefined;
}

/**
 * Traduit le filtre de plage de dates de la toolbar (`<field>After`, `<field>Before`,
 * au format `yyyy-MM-dd`) en paramètres API. Une date de fin invalide est ignorée.
 */
function buildDateRangeParams(
  columnFilters: ColumnFiltersState,
  field: 'sentAt' | 'statusUpdatedAt'
): Record<string, string> {
  const after = getFilterValue<string>(columnFilters, `${field}After`);
  const before = getFilterValue<string>(columnFilters, `${field}Before`);
  const beforeDate = before ? parse(before, 'yyyy-MM-dd', new Date()) : null;

  return {
    ...(after ? { [`${field}[after]`]: after } : {}),
    // `[before]` compare à minuit : on décale d'un jour et bascule sur `strictly_before`
    // pour inclure la journée de fin entière.
    ...(beforeDate && isValid(beforeDate)
      ? { [`${field}[strictly_before]`]: format(addDays(beforeDate, 1), 'yyyy-MM-dd') }
      : {})
  };
}

function buildSortParams(sorting: SortingState): Record<string, 'asc' | 'desc'> {
  const params: Record<string, 'asc' | 'desc'> = {};

  for (const sort of sorting) {
    if (SORTABLE_FIELDS.includes(sort.id)) {
      params[`order[${sort.id}]`] = sort.desc ? 'desc' : 'asc';
    }
  }

  return params;
}

type EmailHistoryTableProps = {
  source: EmailHistorySource;
};

/**
 * Historique des e-mails envoyés aux membres d'une société (tous rôles confondus) ou à un
 * utilisateur. Filtres, tri et pagination côté serveur.
 */
export function EmailHistoryTable({ source }: EmailHistoryTableProps) {
  const { t } = useTranslation();

  async function emailsListQuery(): Promise<EmailsQueryResult> {
    // Sans code, l'API renverrait les e-mails de tout le workspace : on n'appelle pas.
    if (!source.uniqueCode) return { data: [] };

    const subject = getFilterValue<string>(columnFilters, 'subject');
    const to = getFilterValue<string>(columnFilters, 'to');
    const status = getFilterValue<string[]>(columnFilters, 'status');

    const params: ApiAdminemailsGetCollectionParams = {
      ...(source.type === 'company'
        ? { 'userWorkspace.company.uniqueCode': source.uniqueCode }
        : { 'userWorkspace.uniqueCode': source.uniqueCode }),
      page: pageIndex + 1,
      perPage: pageSize,
      ...(subject ? { subject } : {}),
      ...(to ? { to } : {}),
      ...(status?.length ? { 'status[]': status } : {}),
      ...buildDateRangeParams(columnFilters, 'sentAt'),
      ...buildDateRangeParams(columnFilters, 'statusUpdatedAt'),
      ...buildSortParams(sorting)
    };

    const response = await apiAdminemailsGetCollection(params);
    return { data: response.data ?? [], pagination: response.pagination };
  }

  const emailsQuery = useQuery<EmailsQueryResult>({
    queryKey: getEmailHistoryQueryKey(source),
    queryFn: emailsListQuery,
    enabled: Boolean(source.uniqueCode),
    // La table repart de son état initial à chaque montage : ne pas réafficher une page ou
    // des filtres d'une visite précédente le temps du refetch.
    gcTime: 0
  });

  const filterFields = useEmailHistoryFilterFields();

  const { table } = useDataTable<EmailEmailReadModel, unknown>({
    data: emailsQuery.data?.data ?? [],
    columns: useEmailHistoryColumns(),
    filterFields,
    pageCount: emailsQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: true
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const sorting = table.getState().sorting;
  const columnFilters = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });
  const { refetch } = emailsQuery;

  // `useDataTable` garde pagination/tri/filtres en état interne : React Query ne peut pas
  // les porter dans la `queryKey`, d'où le refetch explicite (même pattern que changelogs).
  useEffect(() => {
    const prevValues = prevValuesRef.current;

    if (
      pageIndex !== prevValues.pageIndex ||
      pageSize !== prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) !== JSON.stringify(prevValues.sorting)
    ) {
      refetch();
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting, refetch]);

  useOrvalQueryErrorToast(emailsQuery, t('toast.error.label'));

  if (emailsQuery.isLoading) {
    return (
      <DataTableSkeleton columnCount={5} cellWidths={['16rem', '14rem', '8rem', '8rem', '8rem']} />
    );
  }

  return (
    <>
      <DataTableAdvancedToolbar table={table} filterFields={filterFields} />
      <DataTable
        table={table}
        totalItems={emailsQuery.data?.pagination?.totalItems}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
      />
    </>
  );
}
